import React, { useState } from 'react'
import { DatePicker, Button, Select } from 'antd'
import { DownloadOutlined } from '@ant-design/icons'
import CommonTablePage, { CommonTablePagePropsInterface } from '../../../../components/common/common-table/common-table-page'
import { useDocumentTitle } from '../../../../services/title/useDocumentTitle'
import moment from 'moment'
import DeliveryDetailsReportColumnModel from './delivery-details-report-column-model'
import './delivery-details-report.css'

const { RangePicker } = DatePicker
const { Option } = Select

interface DateRange {
  startDate: string
  endDate: string
}

const DeliveryDetailsReportPage = () => {
  useDocumentTitle()
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: moment().startOf('month').format('YYYY-MM-DD'),
    endDate: moment().endOf('month').format('YYYY-MM-DD')
  })

  /**
   * 将本地日期范围转换为后台 API 需要的 UTC 时间戳
   *
   * 问题：
   * - 用户选择的是本地日期（如 2024-01-01）
   * - 后台 createdAt 存储的是 UTC 时间
   * - 需要发送完整的 UTC 时间戳（包括时分秒）
   *
   * 解决方案：
   * - 将本地日期的起始时间（00:00:00）转换为 UTC 时间戳
   * - 将本地日期的结束时间（23:59:59）转换为 UTC 时间戳
   * - 例如：本地 2024-01-01 00:00:00 (UTC+10) = 2023-12-31T14:00:00Z
   */
  const getDateRangeForApi = () => {
    const localStart = moment(dateRange.startDate).startOf('day')  // 本地 00:00:00
    const localEnd = moment(dateRange.endDate).endOf('day')        // 本地 23:59:59

    // 转换为 UTC ISO 格式时间戳
    const utcStartTimestamp = localStart.utc().format('YYYY-MM-DDTHH:mm:ss[Z]')
    const utcEndTimestamp = localEnd.utc().format('YYYY-MM-DDTHH:mm:ss[Z]')

    return { start: utcStartTimestamp, end: utcEndTimestamp }
  }
  const [selectedPeriod, setSelectedPeriod] = useState<string>('thisMonth')
  const [triggerResetData, setTriggerResetData] = useState<number>(Date.now())
  const [tableData, setTableData] = useState<any[]>([])

  const handleDateRangeChange = (dates: any) => {
    if (dates && dates[0] && dates[1]) {
      setDateRange({
        startDate: dates[0].format('YYYY-MM-DD'),
        endDate: dates[1].format('YYYY-MM-DD')
      })
      setTriggerResetData(Date.now())
      setSelectedPeriod('') // 清空预设选择
    }
  }

  // 下拉选择器的日期预设
  const datePresets: Record<string, () => { start: string; end: string }> = {
    thisMonth: () => ({
      start: moment().startOf('month').format('YYYY-MM-DD'),
      end: moment().endOf('month').format('YYYY-MM-DD')
    }),
    lastMonth: () => ({
      start: moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
      end: moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD')
    }),
    twoMonthsAgo: () => ({
      start: moment().subtract(2, 'month').startOf('month').format('YYYY-MM-DD'),
      end: moment().subtract(2, 'month').endOf('month').format('YYYY-MM-DD')
    }),
    thisYear: () => ({
      start: moment().startOf('year').format('YYYY-MM-DD'),
      end: moment().endOf('year').format('YYYY-MM-DD')
    }),
    oneYear: () => ({
      start: moment().subtract(1, 'year').format('YYYY-MM-DD'),
      end: moment().format('YYYY-MM-DD')
    })
  }

  // 处理下拉选择
  const handlePeriodChange = (value: string) => {
    setSelectedPeriod(value)
    if (value && datePresets[value]) {
      const range = datePresets[value]()
      setDateRange({
        startDate: range.start,
        endDate: range.end
      })
      setTriggerResetData(Date.now())
    }
  }

  // 导出 CSV
  const handleExportExcel = async () => {
    try {
      // 使用表格中已加载的数据
      if (!tableData || tableData.length === 0) {
        alert('No data to export. Please load data first.')
        return
      }

      // CSV 列头
      const headers = ['Packing Slip No', 'Order No', 'Delivered Date', 'Customer', 'Delivery Address', 'Product Code', 'Product Name', 'Carton Qty', 'Delivery Method']

      // 转换数据为 CSV 格式
      const csvRows = [
        headers.join(','),
        ...tableData.map((row: any) => [
          `"${(row.packingSlipNo || '').replace(/"/g, '""')}"`,
          `"${(row.orderNo || '').replace(/"/g, '""')}"`,
          `"${row.deliveredDate ? moment.utc(row.deliveredDate).local().format('DD/MM/YYYY') : ''}"`,
          `"${(row.customer || '').replace(/"/g, '""')}"`,
          `"${(row.deliveryAddress || '').replace(/"/g, '""')}"`,
          `"${(row.productCode || '').replace(/"/g, '""')}"`,
          `"${(row.productName || '').replace(/"/g, '""')}"`,
          `"${row.cartonQty || 0}"`,
          `"${(row.deliveryMethod || '').replace(/"/g, '""')}"`
        ].join(','))
      ]

      // 创建 Blob 并下载
      const csvContent = csvRows.join('\n')
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `DeliveryDetails_${dateRange.startDate}_to_${dateRange.endDate}.csv`
      a.style.display = 'none'
      document.body.appendChild(a)
      a.click()
      setTimeout(() => {
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
      }, 100)
    } catch (error:any) {
      console.error('Export failed:', error)
      alert('Export failed: ' + error.message)
    }
  }

  // 获取 API URL，处理时区转换
  const getApiUrl = () => {
    const apiDateRange = getDateRangeForApi()
    return `Dispatching/GetDeliveryDetailsByDateRange?start=${apiDateRange.start}&end=${apiDateRange.end}`
  }

  const commonTablePageProps: CommonTablePagePropsInterface = {
    getAllUrl: getApiUrl(),
    title: 'Delivery Details Report',
    column: DeliveryDetailsReportColumnModel(),
    isNotEditable: true,
    isNotDeletable: true,
    isNotAddable: true,
    columnsButton: true,
    triggerResetData: triggerResetData,
    mappingRenderData: (data: any) => {
      const result = Array.isArray(data) ? data : (data?.data || [])
      setTableData(result) // 保存数据用于导出
      return result
    }
  }

  // 调试日志
  const apiDateRange = getDateRangeForApi()
  console.log('Delivery Details Report - Date Range (UTC with Time):', {
    userSelected: `${dateRange.startDate} to ${dateRange.endDate}`,
    localInterpretation: {
      start: moment(dateRange.startDate).format('YYYY-MM-DD HH:mm:ss'),
      end: moment(dateRange.endDate).add(1, 'day').add(-1, 'second').format('YYYY-MM-DD HH:mm:ss'),
    },
    utcTimestamps: {
      start: moment(dateRange.startDate).utc().format('YYYY-MM-DDTHH:mm:ss[Z]'),
      end: moment(dateRange.endDate).add(1, 'day').add(-1, 'second').utc().format('YYYY-MM-DDTHH:mm:ss[Z]'),
    },
    sendingToApi: {
      start: apiDateRange.start,
      end: apiDateRange.end,
      url: getApiUrl()
    },
    explanation: 'Sending full UTC timestamps with time to backend'
  })

  return (
    <div className="delivery-details-report-page">
      <div className="date-range-picker">
        <span>Date Range: </span>
        <Select
          style={{ width: 180 }}
          placeholder="Select Period"
          value={selectedPeriod}
          onChange={handlePeriodChange}
          allowClear
        >
          <Option value="thisMonth">This Month</Option>
          <Option value="lastMonth">Last Month</Option>
          <Option value="twoMonthsAgo">2 Months Ago</Option>
          <Option value="thisYear">This Year</Option>
          <Option value="oneYear">1 Year</Option>
        </Select>
        <RangePicker
          value={[moment(dateRange.startDate), moment(dateRange.endDate)]}
          onChange={handleDateRangeChange}
          format="YYYY-MM-DD"
          allowClear={false}
          style={{ marginLeft: '12px' }}
        />
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={handleExportExcel}
          style={{ marginLeft: '16px' }}
        >
          <span style={{ color: 'white' }}>Export to CSV</span>
        </Button>
      </div>
      <CommonTablePage {...commonTablePageProps} />
    </div>
  )
}

export default DeliveryDetailsReportPage
